import React, { useEffect } from "react";
import { Routes, Route, useLocation, Navigate } from "react-router-dom";
import Dashboard from "./pages/Dashboard";
import ReleaseNotes from "./pages/ReleaseNotes";
import ReleaseNote from "./pages/ReleaseNote";
import ReleaseItem from "./pages/ReleaseItem";
import MissingArticles from "./pages/MissingArticles";
import MissingArticle from "./pages/MissingArticle";
import StatisticsPage from "./pages/Statistics";
import NotFoundPage from "./pages/NotFoundPage";
import LoginPage from "./pages/Login";
import Account from "./pages/Account";
import UserManagement from "./pages/UserManagement";
import APIDocs from "./pages/APIDocs";
import PatchNotes from "./pages/PatchNotes";
import ArticlesListPage from "./pages/ArticlesListPage";
import ArticleDetailPage from "./pages/ArticleDetailPage";
import Pipelines from "./pages/Pipelines";
import Pipeline from "./pages/Pipeline";
import { BreadcrumbProvider } from "@/contexts/BreadcrumbContext";
import { useAuth } from "@/hooks/useAuth";
import { AuthProvider } from "@/contexts/AuthContext";
import { WebSocketProvider } from "@/contexts/WebsocketContext";
import { ToastProvider } from "@/contexts/ToastContext";
import { useWebSocketNotifications } from "@/hooks/useWebSocketNotifications";
import { ProtectedRoute } from "@/components/protected-route";
import { API_BASE_URL } from "@/config";
import Sidebar from "./components/Sidebar";
import { SidebarProvider, useSidebar } from "@/contexts/SidebarContext";

// WebSocket notification handler component
function WebSocketNotificationHandler() {
  useWebSocketNotifications();
  return null;
}

// Helper component to scroll to top on route change
function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
}

// Main content wrapper that adjusts its margin based on sidebar state
function MainContentWrapper({ children }: { children: React.ReactNode }) {
  const { isSidebarOpen, isMobileView } = useSidebar();

  // On mobile, sidebar is an overlay, so no margin needed for content.
  // On desktop, adjust margin based on sidebar state.
  const marginLeftClass = isMobileView
    ? "ml-0"
    : isSidebarOpen
      ? "lg:ml-32"
      : "lg:ml-16";

  return (
    <div
      className={`flex flex-1 flex-col transition-all duration-300 ease-in-out ${marginLeftClass}`}
    >
      {children}
    </div>
  );
}

// App layout component
function AppLayout({ children }: { children: React.ReactNode }) {
  return (
    <BreadcrumbProvider>
      <ScrollToTop />
      <div className="flex min-h-screen bg-base-100">
        <Sidebar />
        <MainContentWrapper>
          <div className="@container/main flex flex-1 flex-col">
            <main className="flex-1 p-4 md:p-6">{children}</main>
          </div>
        </MainContentWrapper>
      </div>
    </BreadcrumbProvider>
  );
}

// Wrapper for UserManagement to include ProtectedRoute logic cleanly
const UserManagementPage = () => (
  <ProtectedRoute requireAdmin={true}>
    <UserManagement apiBaseUrl={API_BASE_URL} />
  </ProtectedRoute>
);

function AppRoutes() {
  const { isAuthenticated } = useAuth();

  const LoginRoute = () => {
    return isAuthenticated ? (
      <Navigate to="/" replace />
    ) : (
      <LoginPage apiBaseUrl={API_BASE_URL} />
    );
  };

  return (
    <Routes>
      <Route path="/login" element={<LoginRoute />} />
      <Route
        path="/*"
        element={
          <ProtectedRoute>
            <AppLayout>
              <Routes>
                <Route
                  path="/"
                  element={<Dashboard apiBaseUrl={API_BASE_URL} />}
                />
                <Route
                  path="release-notes"
                  element={<ReleaseNotes apiBaseUrl={API_BASE_URL} />}
                />
                <Route
                  path="release-notes/:noteId"
                  element={<ReleaseNote apiBaseUrl={API_BASE_URL} />}
                />
                <Route
                  path="release-notes/:noteId/item/:itemId"
                  element={<ReleaseItem apiBaseUrl={API_BASE_URL} />}
                />
                <Route
                  path="missing-articles"
                  element={<MissingArticles apiBaseUrl={API_BASE_URL} />}
                />
                <Route
                  path="missing-articles/item/:itemId"
                  element={<MissingArticle apiBaseUrl={API_BASE_URL} />}
                />
                <Route
                  path="statistics"
                  element={<StatisticsPage apiBaseUrl={API_BASE_URL} />}
                />
                <Route
                  path="account"
                  element={<Account apiBaseUrl={API_BASE_URL} />}
                />
                <Route
                  path="user-management"
                  element={<UserManagementPage />}
                />
                <Route
                  path="docs"
                  element={<APIDocs apiBaseUrl={API_BASE_URL} />}
                />
                <Route path="patch-notes" element={<PatchNotes />} />
                <Route
                  path="articles"
                  element={<ArticlesListPage apiBaseUrl={API_BASE_URL} />}
                />
                <Route
                  path="articles/:articleId"
                  element={<ArticleDetailPage apiBaseUrl={API_BASE_URL} />}
                />
                <Route
                  path="pipeline"
                  element={<Pipelines apiBaseUrl={API_BASE_URL} />}
                />
                <Route
                  path="pipeline/:pipelineId"
                  element={<Pipeline apiBaseUrl={API_BASE_URL} />}
                />
                <Route path="*" element={<NotFoundPage />} />
              </Routes>
            </AppLayout>
          </ProtectedRoute>
        }
      />
    </Routes>
  );
}

function App() {
  // Construct wsUrlBase from API_BASE_URL
  // Ensure API_BASE_URL is the correct base for your API, e.g., https://api.svenssonmedia.com/api
  const wsScheme = API_BASE_URL.startsWith("https") ? "wss" : "ws";
  // Remove /api suffix if present, as ws path is /api/ws/general
  const apiUrlWithoutTrailingSlash = API_BASE_URL.endsWith("/")
    ? API_BASE_URL.slice(0, -1)
    : API_BASE_URL;
  const domainAndPort = apiUrlWithoutTrailingSlash
    .replace(/^https?:\/\//, "")
    .split("/")[0];
  const wsUrlBase = `${wsScheme}://${domainAndPort}/api`; // Assuming your WS is at /api/ws/general relative to the domain

  return (
    <AuthProvider apiBaseUrl={API_BASE_URL}>
      <WebSocketProvider wsUrlBase={wsUrlBase}>
        <ToastProvider>
          <SidebarProvider>
            <WebSocketNotificationHandler />
            <AppRoutes />
          </SidebarProvider>
        </ToastProvider>
      </WebSocketProvider>
    </AuthProvider>
  );
}

export default App;
